import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AutocompleteV2Suggestion } from '~/component/form/autocomplete-v2/interfaces/suggestion.interface';
import { InputStyleEnum } from '~/component/form/input/enums/style.enum';
import { PlaceItemInterface } from '~/attendee/components/shared/city-autocomplete/interface/city-autocomplete.interface';

import { DOMService } from '~/core/services/dom/dom.service';
import { MapsService } from '~/core/services/maps/maps.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

import { DistanceUnitEnum } from './enums/distance-unit.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

import { environment } from 'src/environments/environment';

// corresponding to default distance in app.config.distance
const DEFAULT_RADIUS_VALUE = 3;

@Component({
  selector: 'app-attendee-city-autocomplete',
  templateUrl: './city-autocomplete.component.html',
})
export class AttendeeCityAutocompleteComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() public ariaLabel: string;
  @Input() public defaultPlaceId?: string;
  @Input() public defaultPlaceValue?: string;
  @Input() public defaultRadiusValue?: number;
  @Input() public inputStyle = InputStyleEnum.Shadowed;
  @Input() public label?: string;
  @Input() public placeholder: string;

  @Output() public isCleared = new EventEmitter<void>();
  @Output() public selectedPlace = new EventEmitter<{
    city: string;
    placeId: string;
  }>();
  @Output() public selectedRadius = new EventEmitter<number>();

  private readonly subscriptions = new Subscription();

  public readonly distanceValueCollection = [1, 5, 10, 20, 50, 100, 200];
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly icon = VtmnPlayIconTypeEnum;

  private allowedTypes: string[];
  private distanceUnit: string;
  private skipUpdate = false;

  public displayRadiusSearch: boolean;
  public hasGeolocation: boolean;
  public hasRadiusSearch: boolean;
  public place = new FormControl();
  public radius = new FormControl();
  public suggestions: AutocompleteV2Suggestion<string>[] = [];

  constructor(
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly mapsService: MapsService,
    private readonly domService: DOMService,
    private readonly paramethorService: ParamethorService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.defaultPlaceId?.currentValue !==
        changes.defaultPlaceId?.previousValue &&
      !changes.defaultPlaceId?.currentValue
    ) {
      this.place.setValue(null);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.distanceUnit = 'metric';
    this.hasGeolocation = true;
    this.hasRadiusSearch = true;

    this.subscriptions.add(
      this.place?.valueChanges.subscribe((place) => {
        this.selectedPlace.emit({
          city: place?.value,
          placeId: place?.placeId,
        });
      })
    );

    this.allowedTypes = this.paramethorService
      .get(ParamethorNameEnum.AutocompleteAllowedTypes, 'locality,sublocality')
      .split(',');

    const index =
      this.defaultRadiusValue &&
      this.distanceValueCollection.findIndex(
        (value) => value === this.defaultRadiusValue
      );
    if (this.hasRadiusSearch) {
      this.radius.setValue(index >= 0 ? index : DEFAULT_RADIUS_VALUE);
    }

    if (this.defaultPlaceValue) {
      this.place?.setValue({
        text: `${this.defaultPlaceValue} ${this.rangeDisplayValue}`,
        value: this.defaultPlaceValue,
        placeId: this.defaultPlaceId,
      });

      this.displayRadiusSearch = true;
    }
  }

  public get hasAriaLabel(): boolean {
    return Boolean(!this.label && this.ariaLabel);
  }

  public get rangeDisplayValue(): string {
    return (
      this.distanceUnit === DistanceUnitEnum.METRIC &&
      `(${this.distanceValueCollection[this.radius.value]} km)`
    );
  }

  public clearPlaceInput(): void {
    this.suggestions = [];
    this.place.reset();
    this.displayRadiusSearch = false;

    if (this.hasRadiusSearch) {
      this.radius.setValue(DEFAULT_RADIUS_VALUE);
    }

    this.isCleared.emit();
  }

  public geolocalize(): void {
    navigator.geolocation.getCurrentPosition((position) => {
      this.mapsService
        .reverseGeocode(position?.coords?.latitude, position?.coords?.longitude)
        .subscribe((address) => {
          const formattedAddress = `${address.address_components[3]?.long_name}, ${address.address_components[0]?.long_name}`;
          this.place.setValue({
            text: formattedAddress,
            value: formattedAddress,
            placeId: address?.public_id,
            keepListOpen: true,
          });

          this.place.enable();
          this.suggestions = [];
          this.displayRadiusSearch = true;
        });
    });
  }

  public preventLabelBehaviour(event: MouseEvent): void {
    event.preventDefault();
  }

  public selectPlaceItem(item: PlaceItemInterface): void {
    this.skipUpdate = true;

    this.place.setValue({
      placeId: item.placeId,
      text: item.text,
      value: item.value,
      keepListOpen: true,
    });

    this.displayRadiusSearch = true;

    // Prevent autocomplete clickout to be call after the suggestion list is empty //
    this.domService.afterTick(() => {
      this.suggestions = [];
    });
  }

  public setRadius(): void {
    this.place.setValue({
      text: `${this.place?.value?.value} ${this.rangeDisplayValue}`,
      value: `${this.place?.value?.value}`,
      placeId: this.place?.value?.placeId,
    });

    this.selectedRadius.emit(this.distanceValueCollection[this.radius?.value]);
  }

  public suggestionGoogleMaps(suggestion: string): void {
    // prevent empty calls to predictions if no city typed
    if (
      !suggestion ||
      suggestion.trim() === '' ||
      suggestion === this.place?.value?.text
    ) {
      return;
    }

    this.displayRadiusSearch = false;

    // remove the radius string from search string //
    const search = suggestion.split('(')[0];

    this.subscriptions.add(
      this.mapsService
        .getPredictions(
          search,
          environment?.applications?.woosmap?.locale,
          5,
          'locality|postal_code|country'
        )
        .subscribe(
          (predictions: woosmap.map.localities.LocalitiesPredictions[]) => {
            this.suggestions = predictions.map((prediction) => ({
              placeId: prediction.public_id,
              text: prediction.description,
              value: prediction.description,
            }));
          }
        )
    );
  }

  public unskipUpdate(): void {
    this.skipUpdate = false;
  }
}
